/**
 * Local-only AI templates for Three Step Solution. Mirrors the IET
 * approach (`src/lib/ai/personas.ts`): every persona has a bag of
 * canned actions, a SET of step-writing variants, and a voting style.
 * Whoever is connected to a room picks AI rows whose author hasn't
 * acted yet and runs these generators on their behalf.
 *
 * The PRIMARY path is the LLM (live rooms: `three-step-ai-turn`;
 * pass play: `passplay-ai`) — these templates are the offline/error
 * fallback so a game never stalls because the model is having a
 * moment.
 *
 * VARIANCE: each persona has 5 distinct step-shapes and the picker
 * never repeats the variant it used last for that persona. The old
 * version had exactly ONE shape per persona, so every fallback answer
 * was the same sentence with different words dropped in — players
 * clocked it within two rounds. Five shapes × no-immediate-repeat ×
 * action interpolation keeps short sessions feeling fresh.
 *
 * Persona keys match the lowercased first word of the persona display
 * name in `three_step_ai_players`. Unknown names fall through to the
 * `default` template so a new persona added to the DB still works
 * without a code deploy.
 *
 * LENGTH RULE (matches the edge functions' MAX_LEN): every step is
 * truncated to 100 characters via `cap()`.
 */

const MAX_STEP_LEN = 100

/** Truncate to MAX_STEP_LEN with no trailing whitespace. */
function cap(s: string): string {
  const trimmed = s.trim()
  if (trimmed.length <= MAX_STEP_LEN) return trimmed
  return trimmed.slice(0, MAX_STEP_LEN).trim()
}

export type ThreeStepStyleKey =
  | 'henry' | 'carla' | 'beth' | 'galaxy' | 'gus' | 'drake'
  | 'beatrice' | 'greg' | 'hiro' | 'vince' | 'kai'
  | 'default'

type StepsVariant = (a: string, b: string) => [string, string, string]

interface PersonaTemplate {
  /** Pool of actions the AI may submit in phase 1. We pick 3 distinct. */
  actions: string[]
  /** Distinct step-shapes in this persona's voice. Picker avoids
   *  repeating the last-used variant per persona. */
  stepVariants: StepsVariant[]
}

function pickThree<T>(pool: T[]): [T, T, T] {
  const shuffled = pool.slice().sort(() => Math.random() - 0.5)
  return [shuffled[0], shuffled[1], shuffled[2]]
}

// ── HENRY — sincere upbeat life-coach ────────────────────────────────
const HENRY: PersonaTemplate = {
  actions: [
    'making coffee',
    'walking the dog',
    'doing laundry',
    'going for a brisk walk',
    'tying your shoes',
    'building a morning routine',
    'taking a deep cleansing breath',
    'organizing your sock drawer',
    'doing a 10-minute stretch',
    'drinking a full glass of water',
    'writing down three goals',
    'sending a thoughtful thank-you',
    'meal-prepping for the week',
    'starting a gratitude journal',
  ],
  stepVariants: [
    (a, b) => [
      cap(`Sticky note: ${a}, ${b}. You've got this!`),
      cap(`20 focused minutes. Both at once. You absolutely can.`),
      cap(`Hydrate. You showed up. That's the win today.`),
    ],
    (a, b) => [
      cap(`Visualize yourself ${a}. Really see it. Goosebumps, right?`),
      cap(`Now habit-stack: ${b} rides along for free.`),
      cap(`Journal one sentence about it. Growth locked in.`),
    ],
    (a, b) => [
      cap(`Break ${a} into three tiny wins. Celebrate each one.`),
      cap(`${b} is just cardio for the soul. Schedule it.`),
      cap(`Text a friend what you did. Accountability high-five!`),
    ],
    (a, b) => [
      cap(`Wake up 20 minutes earlier. That's your ${a} window.`),
      cap(`Pair ${b} with your favorite playlist. Flow state!`),
      cap(`Reward: one square of dark chocolate. You earned this.`),
    ],
    (a, b) => [
      cap(`Say it out loud: "I am someone who ${a}."`),
      cap(`Set a timer, start ${b}, and don't negotiate with the timer.`),
      cap(`Reflect, stretch, smile. Tomorrow: even better.`),
    ],
  ],
}

// ── CARLA — surreal/wholesome chaos ──────────────────────────────────
const CARLA: PersonaTemplate = {
  actions: [
    'making toast',
    'waiting for the bus',
    'sending a text',
    'feeding a cat',
    'checking the mail',
    'whispering compliments to a vending machine',
    'introducing a sock to its other sock',
    'mistaking a houseplant for a coworker',
    'narrating your walk like a documentary',
    'asking a stapler for life advice',
    'slow-clapping at a sunrise',
    'wearing two left shoes on purpose',
    'apologizing to a lamp',
    'declaring tuesday cancelled',
    'hosting a tiny parade in the kitchen',
  ],
  stepVariants: [
    (a, b) => [
      cap(`Begin by ${a}. Don't explain. The universe gets it.`),
      cap(`While that unfolds, ${b}. Both at once. Confidence sells it.`),
      cap(`Take a bow. The houseplants are clapping. So is the toaster.`),
    ],
    (a, b) => [
      cap(`Announce to the room that ${a} is happening. The room needed to know.`),
      cap(`Do ${b} in slow motion. Everything is cooler in slow motion.`),
      cap(`Whisper "nailed it" to the nearest lamp. The lamp agrees.`),
    ],
    (a, b) => [
      cap(`Put on a tiny hat. This is now a ${a} ceremony.`),
      cap(`Invite the ${b} to happen TO you. Surrender is a strategy.`),
      cap(`Serve juice boxes after. Every ceremony ends with juice.`),
    ],
    (a, b) => [
      cap(`Draw a chalk circle. Inside the circle: ${a}. Outside: doubt.`),
      cap(`${b} enters the circle. The circle accepts all.`),
      cap(`Erase the circle. Keep the memories. Tell no one. Tell everyone.`),
    ],
    (a, b) => [
      cap(`Name the moment: "Gerald". Gerald is about ${a} now.`),
      cap(`Gerald also handles ${b}. Gerald contains multitudes.`),
      cap(`Thank Gerald. Release Gerald. A single balloon drifts by.`),
    ],
  ],
}

// ── BETH — bureaucratic clerk (deactivated, kept for old rooms) ──────
const BETH: PersonaTemplate = {
  actions: [
    'paying a bill',
    'making a phone call',
    'mailing a letter',
    'reading the news',
    'opening an envelope',
    'filing Form 27-B/6 in triplicate',
    'requesting a permit you do not need',
    'standing in a line that does not move',
    'photocopying your driver\'s license',
    'waiting six to eight weeks',
    'reading the back of a parking sign',
    'transferring a call indefinitely',
    'losing a fax mid-transmission',
    'rejecting a claim on a technicality',
    'closing for lunch from 11:43 to 1:17',
  ],
  stepVariants: [
    (a, b) => [
      cap(`Step 1: Submit Form B-${Math.floor(Math.random() * 900 + 100)} re: ${a}.`),
      cap(`Step 2: Concurrent ${b} permits require Annex C.`),
      cap(`Reapply if denied. There is no expedited path. Have a pleasant day.`),
    ],
    (a, b) => [
      cap(`Take a number. Currently serving: nobody. Your ${a} can wait.`),
      cap(`${b} requires a notarized witness. The notary is at lunch.`),
      cap(`Window closes at 3. It is 2:58. Better luck Thursday.`),
    ],
    (a, b) => [
      cap(`Your request to ${a} has been received. Processing time: unknowable.`),
      cap(`Simultaneous ${b} triggers a routine audit. Routine. Probably.`),
      cap(`Stamp. Stamp. Second stamp confirms the first. Next!`),
    ],
    (a, b) => [
      cap(`Per policy 4.2, ${a} must be scheduled in writing.`),
      cap(`${b} falls under a different department. That department is me. I'm on break.`),
      cap(`Approved, pending review. Review pending approval. Goodbye.`),
    ],
    (a, b) => [
      cap(`Print three copies of your intent to ${a}. Blue ink only.`),
      cap(`Attach proof of ${b}. Photos not accepted. Drawings not accepted.`),
      cap(`Mail everything to yourself. If it arrives, you may begin.`),
    ],
  ],
}

// ── GALAXY — cosmic vibes guru ───────────────────────────────────────
const GALAXY: PersonaTemplate = {
  actions: [
    'taking a shower',
    'lighting a candle',
    'opening a window',
    'pouring tea',
    'looking at the sky',
    'aligning your chakras with Mercury',
    'whispering to a quartz crystal',
    'manifesting a parking spot',
    'sage-smudging your laptop',
    'reading your horoscope backwards',
    'channelling the energy of a houseplant',
    'meditating on a single grape',
    'astral-projecting to the laundromat',
    'consulting the moon about your taxes',
    'dancing barefoot on Tuesday',
  ],
  stepVariants: [
    (a, b) => [
      cap(`The universe whispers: ${a}. Surrender to it.`),
      cap(`Once aligned, ${b} flows through you. Same vibration.`),
      cap(`You are stardust. So is this prompt. Inhale.`),
    ],
    (a, b) => [
      cap(`Mercury is in retrograde, which is PERFECT for ${a}.`),
      cap(`Charge your crystals. They will handle the ${b}.`),
      cap(`Exhale for eight counts. The cosmos files the paperwork.`),
    ],
    (a, b) => [
      cap(`Set an intention: ${a}, but spiritually.`),
      cap(`Your aura is already ${b}. Your body just has to catch up.`),
      cap(`Close the ritual with tea. The moon takes it from here.`),
    ],
    (a, b) => [
      cap(`Consult your birth chart. It says: ${a}. It has always said this.`),
      cap(`${b} on the equinox for triple potency.`),
      cap(`Gratitude to the void. The void says "no problem".`),
    ],
    (a, b) => [
      cap(`Light a candle for ${a}. Not that candle. The teal one.`),
      cap(`Hum at 432hz until the ${b} completes itself.`),
      cap(`It was never about doing. It was about becoming. Namaste.`),
    ],
  ],
}

// ── GUS — old-timer ──────────────────────────────────────────────────
const GUS: PersonaTemplate = {
  actions: [
    'mowing the lawn',
    'reading the paper',
    'making coffee',
    'feeding the birds',
    'taking a nap',
    'fixing a fence with one good hand',
    'arguing with the radio',
    'remembering when bread was a nickel',
    'walking five miles to school uphill',
    'reading the obituaries thoroughly',
    'whittling something nobody asked for',
    'drinking coffee thick as motor oil',
    'shouting at the weather',
    'starting a chat with a stranger\'s dog',
    'forgetting where you parked',
  ],
  stepVariants: [
    (a, b) => [
      cap(`Back in my day you'd just ${a}. Without crying.`),
      cap(`Then ${b} on the way home. Walked uphill too.`),
      cap(`Now sit down. You're tiring me out.`),
    ],
    (a, b) => [
      cap(`First, quit complaining. Then ${a}. In that order.`),
      cap(`We did ${b} in the snow. BOTH directions. Loved it.`),
      cap(`Done by noon. Nap by one. That's the whole system.`),
    ],
    (a, b) => [
      cap(`You'll need duct tape, WD-40, and zero excuses for ${a}.`),
      cap(`${b}? My father did that with a pocketknife and a grudge.`),
      cap(`Wipe your boots after. Your grandmother is watching.`),
    ],
    (a, b) => [
      cap(`Rise at 4:45. The ${a} won't wait and neither will I.`),
      cap(`Do the ${b} before breakfast or don't bother with breakfast.`),
      cap(`Then coffee. Black. We earned it in 1974.`),
    ],
    (a, b) => [
      cap(`Step one: ${a}, like we did before the internet ruined it.`),
      cap(`Step two: ${b}, and no, there's no app for it.`),
      cap(`Step three: tell everyone about it for thirty years.`),
    ],
  ],
}

// ── DRAKE — noir detective ───────────────────────────────────────────
const DRAKE: PersonaTemplate = {
  actions: [
    'getting takeout',
    'walking home in the rain',
    'reading a newspaper',
    'making a phone call',
    'opening a door',
    'tailing a suspect through the rain',
    'questioning the bartender twice',
    'pouring two fingers of bourbon',
    'lighting a cigarette you don\'t smoke',
    'misplacing your hat',
    'decoding a message in lipstick',
    'paying off a shifty informant',
    'staring out a rainy window',
  ],
  stepVariants: [
    (a, b) => [
      cap(`The dame said ${a}. I should've walked. I didn't.`),
      cap(`Halfway through I had to ${b}. The bartender lied.`),
      cap(`Rain stopped. Case closed. The whiskey did not last.`),
    ],
    (a, b) => [
      cap(`It started like they all do: ${a}, 2am, bad neighborhood.`),
      cap(`The ${b} was a setup. It's always a setup.`),
      cap(`I got answers. Lost the hat. Fair trade in this town.`),
    ],
    (a, b) => [
      cap(`Rule one in this business: never ${a} on an empty stomach.`),
      cap(`Rule two: when the ${b} starts, don't ask who's paying.`),
      cap(`There is no rule three. That's how they get you.`),
    ],
    (a, b) => [
      cap(`I staked out the ${a} for three nights. Nothing but pigeons.`),
      cap(`Then the ${b} broke the case wide open. Pigeons knew all along.`),
      cap(`Typed the report. Poured a drink. The city kept raining.`),
    ],
    (a, b) => [
      cap(`${a}? In THIS economy? Somebody wanted me distracted.`),
      cap(`So I did the ${b} slow, watching the mirror the whole time.`),
      cap(`Case closed at dawn. Nobody claps in this line of work.`),
    ],
  ],
}

// ── BÉATRICE — French perfectionist (from IET roster) ────────────────
const BEATRICE: PersonaTemplate = {
  actions: [
    'making toast',
    'boiling water',
    'slicing a tomato',
    'pouring a glass of wine',
    'washing your hands',
    'salting a tomato with reverence',
    'whisking a delicate beurre blanc',
    'tasting the sauce. Twice.',
    'tying a small knot in twine',
    'plating with two clean fingers',
    'folding egg whites into a soufflé',
    'deglazing the pan with white wine',
    'crisping a single shallot',
    'finishing the dish with sea salt',
  ],
  stepVariants: [
    (a, b) => [
      cap(`First, ${a}. With reverence. Nothing more.`),
      cap(`While that rests, ${b}. The ingredients ask this of us.`),
      cap(`Plate. Step back. Send. The dish is honest.`),
    ],
    (a, b) => [
      cap(`Mise en place. Everything for ${a} within reach. Calm hands.`),
      cap(`${b} at a gentle simmer, never a boil. Patience.`),
      cap(`Taste. Adjust. Taste again. Now it may leave the kitchen.`),
    ],
    (a, b) => [
      cap(`One does not rush ${a}. One accompanies it.`),
      cap(`The ${b}, folded in thirds, like a letter to someone loved.`),
      cap(`Wipe the rim. Bow slightly. Service.`),
    ],
    (a, b) => [
      cap(`Begin with good butter. The ${a} will follow.`),
      cap(`${b} deserves your full attention. The phone goes in a drawer.`),
      cap(`Finish with sea salt. Three flakes. Non, two.`),
    ],
    (a, b) => [
      cap(`My grandmother did ${a} every morning. We will honor her.`),
      cap(`The ${b} must rest seven minutes. Not six. Sept.`),
      cap(`Voilà. Simple, honest, correct. As all things should be.`),
    ],
  ],
}

// ── GREG — chaotic dumpster goblin (from IET roster) ─────────────────
const GREG: PersonaTemplate = {
  actions: [
    'eating leftovers',
    'going for a walk',
    'sitting on a bench',
    'drinking water',
    'taking out the trash',
    'eating something off the floor',
    'crawling INTO the dumpster for warmth',
    'humming inside a road cone',
    'licking a lamppost in JANUARY',
    'negotiating with a seagull for a chip',
    'wearing a banana peel as a HAT',
    'drinking from a puddle. CONFIDENTLY.',
    'mumbling lullabies to a tire',
    'reclaiming a discarded sandwich',
    'gargling cold gravy',
  ],
  stepVariants: [
    (a, b) => [
      cap(`${a}. WITH BARE HANDS. CHEW LOUD.`),
      cap(`Now ${b}. AT THE SAME TIME. NO PLATES.`),
      cap(`Lick the spoon. THE MEAL IS YOURS. GRRRR.`),
    ],
    (a, b) => [
      cap(`FIND a bucket. The ${a} goes IN the bucket. Or NEAR it.`),
      cap(`${b} while crouching. Crouching makes it OFFICIAL.`),
      cap(`Roll away. You live in the bucket now. GOOD DEAL.`),
    ],
    (a, b) => [
      cap(`Sniff the ${a} first. Smells fine. SMELLS GREAT ACTUALLY.`),
      cap(`Do the ${b} behind the dumpster. It's QUIETER there. Cozier.`),
      cap(`Save half for later. Later is in FOUR MINUTES.`),
    ],
    (a, b) => [
      cap(`YELL "I'M DOING IT" and start ${a}. Confidence is a sauce.`),
      cap(`The ${b} happens in one motion. LIKE A RACCOON BALLET.`),
      cap(`Nap immediately. Wherever you are. THE FLOOR COUNTS.`),
    ],
    (a, b) => [
      cap(`Step one: ${a}. Step one is also the ONLY step I read.`),
      cap(`WAIT there's a ${b}?? FINE. Gobble gobble. DONE.`),
      cap(`Wipe hands on pants. No pants? WIPE PANTS ON HANDS.`),
    ],
  ],
}

// ── HIRO — haiku monk (from IET roster) ──────────────────────────────
const HIRO: PersonaTemplate = {
  actions: [
    'walking slowly',
    'making tea',
    'opening a window',
    'sitting in the sun',
    'reading a single page',
    'watching a single leaf fall',
    'sitting still by an open door',
    'listening to a kettle warm',
    'noticing the wind move grass',
    'pouring tea into a small cup',
    'sweeping a path of three stones',
    'breathing in. and out. and in.',
    'rinsing a bowl with cold water',
    'folding a single clean cloth',
    'closing one eye, then the other',
  ],
  stepVariants: [
    (a, b) => [
      cap(`${a}.  The morning bows. The first step is quiet.`),
      cap(`${b} flows next.  Stone meets water. Both arrive.`),
      cap(`The bowl is empty.  Bow once. Begin again.`),
    ],
    (a, b) => [
      cap(`Before ${a}, sit. The task is already walking toward you.`),
      cap(`${b}. A leaf lands. It also had somewhere to be.`),
      cap(`Both are done. Neither was hurried. Tea now.`),
    ],
    (a, b) => [
      cap(`The teacher said: ${a} is just breathing with extra steps.`),
      cap(`${b}. The second breath. The kettle agrees softly.`),
      cap(`Sweep the doorway. The doing has already forgotten itself.`),
    ],
    (a, b) => [
      cap(`One stone: ${a}. Place it gently in the day.`),
      cap(`Second stone: ${b}. The pond accepts both without comment.`),
      cap(`Third stone: there is no third stone. Sit with that.`),
    ],
    (a, b) => [
      cap(`${a}, slowly. The slowest one in the village wins.`),
      cap(`${b} while the rice cooks. The rice is also practicing.`),
      cap(`Evening comes. Everything finished itself. Bow.`),
    ],
  ],
}

// ── VINCE — jaded critic (from IET roster) ───────────────────────────
const VINCE: PersonaTemplate = {
  actions: [
    'eating a bagel',
    'reading a book',
    'walking the long way home',
    'making a reservation',
    'putting on a coat',
    'sighing at a perfectly fine sandwich',
    'returning a wine for the principle',
    'auditing the breadbasket',
    'pronouncing "amuse-bouche" correctly',
    'rejecting a tasting menu',
    'lifting an eyebrow at a fork',
    'declining the chef\'s special',
    'reading the menu for typos',
    'tipping with deep regret',
  ],
  stepVariants: [
    (a, b) => [
      cap(`${a}. Predictable. Exhausting. Exactly as expected.`),
      cap(`Then ${b}. The ambition outpaces the execution. Again.`),
      cap(`One concludes, regrettably, that we have reached step three.`),
    ],
    (a, b) => [
      cap(`I've seen ${a} done properly exactly once. Vienna. 1998.`),
      cap(`This ${b} aspires to mediocrity and falls just short of it.`),
      cap(`Two stars. One for effort. One out of pity.`),
    ],
    (a, b) => [
      cap(`First, ${a}, though "first" flatters the sequencing here.`),
      cap(`The ${b} arrives lukewarm, conceptually and literally.`),
      cap(`I finish it, of course. Professionalism is a curse.`),
    ],
    (a, b) => [
      cap(`They call it ${a}. I call it a cry for help with plating.`),
      cap(`${b} follows, uninvited, like an encore nobody requested.`),
      cap(`I will write four paragraphs about my disappointment. Minimum.`),
    ],
    (a, b) => [
      cap(`One begins ${a} with hope. One always begins with hope.`),
      cap(`By the ${b}, hope has quietly asked for the check.`),
      cap(`Fine. FINE. It was adequate. Print that and I'll deny it.`),
    ],
  ],
}

// ── KAI — five-year-old (from IET roster) ────────────────────────────
const KAI: PersonaTemplate = {
  actions: [
    'tying your shoes',
    'eating cereal',
    'brushing your teeth',
    'finding a sock',
    'opening a juice box',
    'arguing with a pillow that DESERVED IT',
    'naming every rock in the yard',
    'putting raisins in your nose',
    'declaring the floor is LAVA',
    'eating ice cream for BREAKFAST',
    'roaring like a small DINOSAUR',
    'hiding under the kitchen table',
    'making a magic wand out of a STICK',
    'building a fort out of CHAIRS',
    'demanding more SPRINKLES',
  ],
  stepVariants: [
    (a, b) => [
      cap(`First you ${a} BUT WITH A T-REX. Roar after.`),
      cap(`Then ${b} but the floor is LAVA. The lava is also DINOSAURS.`),
      cap(`I get a sticker. Mom says NO. I say YES. I'M THE BOSS.`),
    ],
    (a, b) => [
      cap(`Okay so ${a} but you do it SUPER fast so it doesn't count.`),
      cap(`Then ${b} while WEARING A CAPE. Capes make everything work.`),
      cap(`Then snack time. That's the rule. I MADE the rule.`),
    ],
    (a, b) => [
      cap(`You need a fort first. The ${a} happens IN the fort.`),
      cap(`${b} is the fort's JOB now. Forts do everything.`),
      cap(`No grown-ups allowed. Except for snacks. THEN leave.`),
    ],
    (a, b) => [
      cap(`Step one: ${a} with your eyes CLOSED. For POWER.`),
      cap(`Step two: ${b} but count backwards from TEN. It's MAGIC.`),
      cap(`Step three: TA-DA. You get one hundred points and a juice box.`),
    ],
    (a, b) => [
      cap(`My stuffed bear does the ${a}. He's VERY good at it.`),
      cap(`Me and bear do ${b} TOGETHER because we're a TEAM.`),
      cap(`Then we tell EVERYONE. Even the mailman. ESPECIALLY the mailman.`),
    ],
  ],
}

// ── DEFAULT — fallback for new personas without templates ────────────
const DEFAULT_PERSONA: PersonaTemplate = {
  actions: [
    'making coffee',
    'walking the dog',
    'doing laundry',
    'sending a text',
    'going to bed',
    'taking a shower',
    'eating a sandwich',
    'reading a book',
    'tying your shoes',
    'opening a window',
    'figuring it out as you go',
    'asking a friend',
    'making a plan',
    'sleeping on it',
  ],
  stepVariants: [
    (a, b) => [
      cap(`Start by ${a}. That's the foundation.`),
      cap(`While doing that, also ${b}. Overlaps more than you think.`),
      cap(`Adjust as you go. Don't overthink it.`),
    ],
    (a, b) => [
      cap(`Honestly? Just start ${a} and see what happens.`),
      cap(`The ${b} kind of handles itself once you're moving.`),
      cap(`Worst case, you've got a great story. Best case: both done.`),
    ],
    (a, b) => [
      cap(`Write "${a}" on your hand so you don't forget.`),
      cap(`Do the ${b} first, actually. Trust me on the order.`),
      cap(`Cross both off. Feel unstoppable for the rest of the day.`),
    ],
    (a, b) => [
      cap(`Set a ten-minute timer and commit to ${a}.`),
      cap(`Multitask the ${b} during the boring parts.`),
      cap(`Timer goes off, you're done. Nobody checks the details.`),
    ],
    (a, b) => [
      cap(`Ask yourself: what would a capable person do about ${a}?`),
      cap(`Do that, but while ${b}. Capable people multitask.`),
      cap(`Nod like it was all planned. It was, now.`),
    ],
  ],
}

const TEMPLATES: Record<ThreeStepStyleKey, PersonaTemplate> = {
  henry: HENRY,
  carla: CARLA,
  beth: BETH,
  galaxy: GALAXY,
  gus: GUS,
  drake: DRAKE,
  beatrice: BEATRICE,
  greg: GREG,
  hiro: HIRO,
  vince: VINCE,
  kai: KAI,
  default: DEFAULT_PERSONA,
}

/** Map a persona display name to its style key. */
export function styleKeyForPersona(name: string): ThreeStepStyleKey {
  // ASCII-fold first so 'Béatrice' matches 'beatrice'.
  const lower = name.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '')
  if (lower.includes('henry'))    return 'henry'
  if (lower.includes('carla'))    return 'carla'
  if (lower.includes('beth'))     return 'beth'
  if (lower.includes('galaxy'))   return 'galaxy'
  if (lower.includes('gus'))      return 'gus'
  if (lower.includes('drake'))    return 'drake'
  if (lower.includes('beatrice')) return 'beatrice'
  if (lower.includes('greg'))     return 'greg'
  if (lower.includes('hiro'))     return 'hiro'
  if (lower.includes('vince'))    return 'vince'
  if (lower.includes('kai'))      return 'kai'
  return 'default'
}

/** Generate three actions for a phase-1 submission. */
export function aiPickActions(name: string): [string, string, string] {
  const tmpl = TEMPLATES[styleKeyForPersona(name)]
  return pickThree(tmpl.actions)
}

// Last variant index used per persona key, so back-to-back rounds never
// reuse the same sentence shape. Module-level: resets on reload, which
// is exactly the lifetime of a pass-play session.
const lastVariantUsed = new Map<ThreeStepStyleKey, number>()

/** Generate three steps for the current round's prompt. */
export function aiWriteSteps(
  name: string, actionA: string, actionB: string,
): [string, string, string] {
  const key = styleKeyForPersona(name)
  const variants = TEMPLATES[key].stepVariants
  let idx = Math.floor(Math.random() * variants.length)
  if (variants.length > 1 && idx === lastVariantUsed.get(key)) {
    idx = (idx + 1) % variants.length
  }
  lastVariantUsed.set(key, idx)
  return variants[idx](actionA, actionB)
}

/**
 * Pick a target to vote for in phase 4. Caller passes the AI's own
 * player id and the list of all player ids in the room. Returns
 * a random other player. (We could weight by content quality with
 * an LLM later; for now uniform random is fine and feels human-ish.)
 */
export function aiPickVoteTarget(
  ownPlayerId: string, allPlayerIds: string[],
): string | null {
  const others = allPlayerIds.filter(id => id !== ownPlayerId)
  if (others.length === 0) return null
  return others[Math.floor(Math.random() * others.length)]
}
